import classNames from 'classnames'
import findIndex from 'lodash/findIndex'
import isObject from 'lodash/isObject'
import React, { ReactNode } from 'react'
import ReactSelect from 'react-select'
import { default as ReactSelectCreatable } from 'react-select/creatable'

import { i18n } from '../../domain/index'
import css from './select.css'

export type OptionType = {
  label: string
  value?: any
  options?: OptionType
}

export type Props = {
  options?: Array<any>
  label?: string
  placeholder?: string
  className?: string
  value?: string
  isClearable?: boolean
  onChange?: (...props: any) => any
  isSearchable?: boolean
  isCreatable?: boolean
  onCreateOption?: (...props: any) => any
  formatCreateLabel?: (props: string) => ReactNode
  getOptionLabel?: (...props: any) => any
  getOptionValue?: (...props: any) => any
  getValueOverride?: (...props: any) => any
}

const SELECT_STYLES = {
  control: (base) => ({
    ...base,
    border: '1px solid #DEDEDE',
    borderColor: '#DEDEDE !important',
    borderRadius: '2px',
    boxShadow: 'none',
    backgroundColor: '#fff',
    fontSize: '16px',
    minHeight: '36px',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(0,0,0,0.38)',
    fontSize: '16px',
    lineHeight: '24px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#78A22F' : state.isFocused ? '#E3EDD1' : '#fff',
    color: state.isSelected ? '#fff' : '#464646',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#464646 !important',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
}

export const getValue = (value: any, options: any) => {
  const index = findIndex(options, (option) => (option as any).value === value)
  return index > -1 ? options[index] : null
}

export const getOptions = (options: any): Array<OptionType> => {
  return options.map((option) => {
    return isObject(option) ? option : { label: option, value: option }
  })
}

const defaultFormatCreateLabel = (value: string) => <>{i18n.t('select.createLabel', { value })}</>

const Select = (props: Props) => {
  const options = getOptions(props.options)
  const inputProps = {
    placeholder: props.placeholder,
    options: options,
    onChange: props.onChange,
    value:
      props.getValueOverride !== undefined
        ? props.getValueOverride(props.value, options)
        : getValue(props.value, options),
    isClearable: props.isClearable,
    isSearchable: !!props.isSearchable,
    getOptionValue: props.getOptionValue,
    getOptionLabel: props.getOptionLabel,
    formatCreateLabel: undefined,
    onCreateOption: undefined,
  }

  return (
    <div className={classNames(css.component, props.className)}>
      {props.label && <p className={css.label}>{props.label}</p>}
      {props.isCreatable ? (
        <ReactSelectCreatable
          {...inputProps}
          formatCreateLabel={props.formatCreateLabel ?? defaultFormatCreateLabel}
          onCreateOption={props.onCreateOption}
        />
      ) : (
        <ReactSelect {...inputProps} />
      )}
    </div>
  )
}

type MultiSelectProps = {
  options: Array<OptionType>
  label?: string
  placeholder?: string
  className?: string
  value?: Array<OptionType>
  onChange: (...props: any) => any
  isMulti?: boolean
}

export const MultiSelect = (props: MultiSelectProps) => (
  <div className={classNames(css.component, props.className)}>
    {props.label && <p className={css.label}>{props.label}</p>}
    <ReactSelect styles={SELECT_STYLES} {...props} />
  </div>
)

export default Select
